import { useEffect, useRef, useCallback, useState, useMemo } from "react";
import { RemoveScroll } from "react-remove-scroll";
import { CustomDataTable } from "../../general_components/content_management/reusableItems/customDataTable";
import { PopupWrapped, ContentWrapper } from "../../general_components/popup_manager/popupManager";
import { createStompConnection } from "../../supplementary_components/stompConnection";
import { TrailerPoolManagement, computeSitesWithTrailers, trailersForMap, omitIgnoredTrailerLocations } from "./trailerPoolManagement";
import { localDateToUSFormat } from "../../general_components/content_management/item_page/driver_page_components/payoutAdjustmentsSection";
import { dateStringToLocalDateString } from "../reports/weeklyReports";
import { castTrailerStatus } from "./trailerPageConf";
import styles from "./trailer.module.scss";
import { LuCalendarClock } from "react-icons/lu";
import { addPopup } from "../../supplementary_components/store";
import { API_ROOT, sendRequest } from "../../supplementary_components/requests";
import { LoadingIcon } from "../../supplementary_components/loading_icon";
import { TrailerReservationManagement } from "./trailerReservationManagement";

/** Matches trailer detail form / backend enums; merged into custom table column options when API omits values. */
const TRAILER_STATUS_SELECT_OPTIONS = ["Active", "Out Of Order", "At Shop", "Pending", "Terminated"];

function mergeTrailerStatusSelectOptions(existing) {
	const seenFront = new Set(TRAILER_STATUS_SELECT_OPTIONS.map((v) => castTrailerStatus(v, true)));
	const out = [...TRAILER_STATUS_SELECT_OPTIONS];
	for (const opt of existing ?? []) {
		const fe = castTrailerStatus(opt, true);
		if (!seenFront.has(fe)) {
			seenFront.add(fe);
			out.push(opt);
		}
	}
	return out;
}

function withTrailerStatusOptionsMerged(tableConf) {
	if (!tableConf) return tableConf;
	const rawCols = tableConf.structure?.columns ?? tableConf.columns;
	if (!Array.isArray(rawCols)) return tableConf;
	const nextCols = rawCols.map((col) => {
		const vk = col.valueKey ?? col.id;
		if (vk !== "trailerStatus") return col;
		if (String(col.type ?? "").toLowerCase() !== "select") return col;
		return { ...col, options: mergeTrailerStatusSelectOptions(col.options) };
	});
	if (tableConf.structure?.columns) {
		return { ...tableConf, structure: { ...tableConf.structure, columns: nextCols } };
	}
	return { ...tableConf, columns: nextCols };
}



export const TrailerManagement = ({dataset}) => {
	const setDataRef = useRef();
	const setCustomTableUpdateRef = useRef();
	const bringToTopMethodRef = useRef();
	const clientIdRef = useRef(crypto.randomUUID?.() ?? `client-${Date.now()}`);
	const stompClientRef = useRef(null);
	const idsRef = useRef(null);
	const [connected, setConnected] = useState(false);
	const [reservationAlert, setReservationAlert] = useState(!!dataset?.reservationsAlertExists);
	const sendTrailerMessage = useCallback((payload) => {
		const client = stompClientRef.current;
		if (!client?.connected) return;
		const ids = idsRef.current;
		if (!ids) return;
		const message = {
			...payload,
			clientId: clientIdRef.current,
		};
		client.publish({
			destination: `/app/trailers/${ids.orgId}/${ids.companyId}`,
			body: JSON.stringify(message),
		});
	}, []);
	const excludeTerminatedTrailers = useCallback(
		(item) => castTrailerStatus(item?.trailerStatus, true) === "Terminated",
		[]
	);

	const trailerCustomTableDefaults = useMemo(
		() => ({
			pmDueDate: {
				interpreter: (val) => (val ? localDateToUSFormat(val) : null),
				formatter: (val) => (val ? dateStringToLocalDateString(val) : null),
			},
			trailerStatus: {
				interpreter: (val) => (val ? castTrailerStatus(val, true) : null),
				conditionalFormatting: (val) => {
					switch (val) {
						case "Active":
							return "var(--main-color)";
						case "OutOfOrder":
						case "Out Of Order":
							return "var(--alert-color)";
						case "AtShop":
						case "At Shop":
							return "var(--inProgressYellow)";
						case "Pending":
							return "var(--inProgressBlue)";
						case "Terminated":
							return "var(--light-gray)";
						default:
							return "var(--light-gray)";
					}
				},
				formatter: (val) => castTrailerStatus(val, false),
			},
			currentTruck: {
				conditionalFormatting: (_val, { item, rowKey, overrides }) => {
					const rowOv = overrides?.[rowKey];
					const detached =
						rowOv != null && Object.prototype.hasOwnProperty.call(rowOv, "potentiallyDetached")
							? !!rowOv.potentiallyDetached
							: !!item?.potentiallyDetached;
					return detached ? "var(--alert-color)" : null;
				},
			},
		}),
		[]
	);

	const trailerManagementTableConf = useMemo(
		() => withTrailerStatusOptionsMerged(dataset?.customTableToManageTrailers),
		[dataset?.customTableToManageTrailers]
	);

	useEffect(() => {
		if (!localStorage.getItem("orgId") || !localStorage.getItem("currentCompanyId")) return;

		const connection = createStompConnection({
			getConnectHeaders: () => ({ companyId: localStorage.getItem("currentCompanyId") ?? "" }),
			onConnectionChange: setConnected,
			onConnect: (client) => {
				const companyId = localStorage.getItem("currentCompanyId");
				const orgId = localStorage.getItem("orgId");
				idsRef.current = { orgId, companyId };
				client.subscribe(`/topic/trailers/${orgId}/${companyId}`, function (message) {
					const parsed = JSON.parse(message.body);
					if (!parsed || typeof parsed !== 'object') return;
					if (parsed.ignoredTrailerNumbersUpdate) {
						const ignored = Array.isArray(parsed.ignoredTrailerNumbers) ? parsed.ignoredTrailerNumbers : [];
						const patches = parsed.locationPatches && typeof parsed.locationPatches === 'object'
							? parsed.locationPatches
							: {};
						setDataRef.current?.(prev => {
							const merged = { ...(prev.trailerLocations ?? {}) };
							for (const [k, v] of Object.entries(patches)) {
								if (v == null) delete merged[k];
								else merged[k] = v;
							}
							const filtered = omitIgnoredTrailerLocations(merged, ignored);
							const serverTime = prev.serverTime;
							const ds = prev.datasetSites ?? [];
							const sites = (computeSitesWithTrailers(ds, filtered) ?? []).map(s => ({ ...s, _serverTime: serverTime }));
							return {
								...prev,
								ignoredTrailerNumbers: ignored,
								trailerLocations: filtered,
								sites,
								trailers: trailersForMap(filtered),
							};
						});
						return;
					}
					if (parsed.customDataUpdate || parsed.structuralChange) {
						if (parsed.senderSessionId === client.sessionId || parsed.clientId === clientIdRef.current) {
							return;
						}
						setCustomTableUpdateRef.current?.(parsed);
						return;
					}
					setCustomTableUpdateRef.current?.(parsed);
					setDataRef.current?.(prev => {
						const ignored = prev.ignoredTrailerNumbers ?? [];
						const ignoredSet = new Set(ignored);
						const merged = { ...prev.trailerLocations };
						for (const [k, v] of Object.entries(parsed)) {
							if (ignoredSet.has(k)) continue;
							if (v == null) delete merged[k];
							else merged[k] = v;
						}
						const serverTime = prev.serverTime;
						const ds = prev.datasetSites ?? [];
						const sites = (computeSitesWithTrailers(ds, merged) ?? []).map(s => ({ ...s, _serverTime: serverTime }));
						return {
							...prev,
							trailerLocations: merged,
							sites,
							trailers: trailersForMap(merged),
						};
					});
				});
			},
		});
		stompClientRef.current = connection.client;
		connection.start();
		return () => {
			connection.stop();
			stompClientRef.current = null;
			idsRef.current = null;
			setConnected(false);
		};
	}, []);

	return <>
		{!connected ? (
			<RemoveScroll>
				<PopupWrapped>
					<ContentWrapper $customWidth={"500px"}>
						<div className={styles.noConnection}>Connecting...</div>
					</ContentWrapper>
				</PopupWrapped>
			</RemoveScroll>
		) : null}
		<div className={`${styles.manageReservationsButton} ${reservationAlert ? styles.hasAlert : ""}`}
		onClick={()=>{
			addPopup({
                content:TrailerReservationManagement,
                popupData: {
                    setReservationAlert: setReservationAlert
                },
                customWidth: "90%",
                customMaxWidth: "1700px",
                customMinWidth: "880px",
                EZCloseAllowed:true
            })
		}}
		><LuCalendarClock /></div>
		<TrailerPoolManagement dataset={dataset} setDataRef={setDataRef} bringToTopMethodRef={bringToTopMethodRef} />
		<CustomDataTable
			itemCategory="trailer"
			itemsArray={dataset?.allTrailersOfCompany}
			tableConf={trailerManagementTableConf}
			identifierColumnName="trailerNumber"
			setCustomTableUpdateRef={setCustomTableUpdateRef}
			broadCastMessage={sendTrailerMessage}
			bringToTopRef={bringToTopMethodRef}
			currentTime={dataset.currentTime}
			defaultValueByColumn={trailerCustomTableDefaults}
			excludeRow={excludeTerminatedTrailers}
		/>
	</>
}

export const TrailerManagementPage = () => {
	const [dataset, setDataset] = useState(null);
	const [error, setError] = useState(false);
	useEffect(() => {
		const controller = new AbortController();
		sendRequest(
			'GET',
			`${API_ROOT}trailers?pageNumber=0&category=Trailer Management`,
			controller.signal,
			(data) => setDataset(data?.dataset ?? null),
			() => setError(true),
			null,
		);
		return () => controller.abort();
	}, []);
	if (error) return <div className="errorMessage">Something went wrong</div>;
	if (!dataset) return <LoadingIcon state height="10rem" />;
	return <TrailerManagement dataset={dataset} />;
};